import { constants } from '@config';
import AbstractElement from './abstractElement';

export default class TextDomSetter extends AbstractElement {
  constructor (text, textareaDom) {
    super();

    this.text = text;
    this.textareaDom = textareaDom;
    this.textClass = text.dom?.classList?.value || '';
    this.tspans = [...text.dom.querySelectorAll(':scope > tspan, :scope > text')];
  }

  getDataFromDom () {
    const wordSpacing = this.getWordSpacing();
    const autoAdjustable = this.getAutoAdjustable();
    const fontSize = this.getFontSize();

    return {
      fill: this.getDomAttribute('fill', '#000000'),
      highlight: this.getHighlight(),
      stroke: this.getDomAttribute('stroke', 'none'),
      stroke_width: this.getDomAttribute('stroke-width', null),
      stroke_dasharray: this.getDomAttribute('stroke-dasharray', null),
      font_family: this.getFontFamily(),
      custom_font_id: this.getDomAttribute('custom-font-id', null),
      font_stretch: this.getDomAttribute('font-stretch', null),
      data_paragraph_style: this.getDomAttribute('data-paragraph-style', null),
      data_meta: this.getDomAttribute('data-meta', null),
      font_size: this.getFontSize(),
      text_anchor: this.getTextAnchor(),
      paragraph_spacing: this.getParagraphSpacing(),
      word_spacing: wordSpacing,
      letter_spacing: this.getLetterSpacing(wordSpacing),
      line_spacing: this.getLineSpacing(fontSize),
      line_margin: this.getLineMargin(),
      first_line_margin: this.getFirstLineMargin(),
      right_margin: this.getRightMargin(),
      text_decoration: this.getDomAttribute('text-decoration', null),
      font_weight: this.getDomAttribute('font-weight', null),
      font_style: this.getDomAttribute('font-style', null),
      auto_adjustable: this.getAutoAdjustable(),
      original_font_size: this.getOriginalFontSize(autoAdjustable, fontSize),
      vertical_offset: this.getDomAttribute('data-vertical-offset', null),
      columns: this.getTextColumns(),
      columns_gap: this.getTextColumnsGap(),
      columns_balance: this.getTextColumnsBalance(),
      direction: this.getDomAttribute('direction', null),
      orderedFill: this.getOrderedFill(),
      orderedWidth: this.getOrderedWidth(),
      bulletFill: this.getBulletFill(),
      bulletWidth: this.getBulletWidth(),
      isTextOverflow: this.getTextOverflow()
    };
  }

  getDomAttribute (attribute, defaultValue) {
    const textAttribute = this.text.dom.getAttribute(attribute);
    const textareaAttribute = this.textareaDom.getAttribute(attribute);

    return textAttribute || textareaAttribute || defaultValue;
  }

  getFontFamily () {
    let fontFamily = this.getDomAttribute('font-family', 'Lato');
    const containsNumber = /\b[0-9]+\b/g;

    if ((fontFamily.indexOf(' ') > 0 || containsNumber.test(fontFamily)) && !fontFamily.match(/^'(.+)'$/)) {
      fontFamily = '\'' + fontFamily.replace(/'/g, '\\\'') + '\'';
    }

    return fontFamily;
  }

  getHighlight () {
    const highlight = this.getDomAttribute('highlight', null);
    return highlight === 'none' ? null : highlight;
  }

  getFontSize () {
    const fontSizeText = this.getDomAttribute('font-size', null);
    const fontSize = fontSizeText && fontSizeText.replace(/[^0-9.]+/, '');
    return parseFloat(fontSize) || 22;
  }

  getTextAnchor () {
    const textAnchor = this.getDomAttribute('text-anchor', 'start');
    return textAnchor === 'justify' ? 'justify_start' : textAnchor;
  }

  getAutoAdjustable () {
    const isSubElement = this.textareaDom.matches('.subelement');
    const isTextAutoAdjustable = this.text.dom.classList.contains('auto-adjustable');
    const isTextareaAutoAdjustable = this.textareaDom.classList.contains('auto-adjustable');

    return (isSubElement && isTextAutoAdjustable) || isTextareaAutoAdjustable;
  }

  getOriginalFontSize (autoAdjustable, fontSize) {
    const originalFontSize = this.getDomAttribute('data-original-font-size', null);
    const shouldGetDefaultSize = autoAdjustable && !originalFontSize;

    return shouldGetDefaultSize
      ? fontSize
      : parseFloat(originalFontSize) || null;
  }

  getLetterSpacing (wordSpacing) {
    const defaultDx = 0;
    const match = this.textClass.match(/letter-spacing-([+-]?\d*\.?\d+)/i);
    let letterSpacing = match && Number.parseFloat(match[1]);

    if (!letterSpacing) {
      letterSpacing = Number.parseFloat(this.getDomAttribute('letter-spacing', defaultDx));
    }

    if (!letterSpacing && this.tspans.length > 0) {
      letterSpacing = Number.parseFloat(this.tspans[0].getAttribute('letter-spacing'));
    }

    if (!letterSpacing) {
      letterSpacing = this.calculateLetterSpacingForChildrenTexts(wordSpacing, defaultDx);
    }

    return letterSpacing || defaultDx;
  }

  calculateLetterSpacingForChildrenTexts (wordSpacing, defaultDx) {
    const { dom } = this.text;
    const domHasChildTextWithDxAttribute = dom.querySelector('text[dx]');
    const textAnchor = dom.getAttribute('text-anchor');

    if (!domHasChildTextWithDxAttribute) return;

    const round = (number) => number ? Math.round(number * 100) / 100 : defaultDx;
    let isUnique = true;

    const letterSpacing = [...dom.children].flatMap((element) => {
      const dxForLetters = (element.getAttribute('dx') || `${defaultDx}`).split(' ');

      // Do not store letter spacing for bullet
      const list = element.querySelector('.bullets') || element.querySelector('.ordered');
      const listLength = list?.textContent.length || 0;
      const mainAnchor = element.getAttribute('text-anchor') || textAnchor;
      const isJustify = mainAnchor?.includes('justify');

      const dx = [];
      const content = element.textContent;
      for (let i = listLength; i < content.length; i++) {
        const isSpace = /\s/.test(content[i]) && wordSpacing;
        const letterDx = isJustify || isSpace ? defaultDx : round(dxForLetters[i] || defaultDx);
        dx.push(letterDx);
        isUnique = isUnique && letterDx === defaultDx;
      }

      return dx;
    });

    return isUnique ? letterSpacing[0] : letterSpacing;
  }

  getParagraphSpacing () {
    const match = this.textClass.match(/paragraph-spacing-([-+]?[0-9]*\.?[0-9]+)/i);
    if (match) return parseFloat(match[1]);

    const dataParagraphDy = this.text.dom.getAttribute('data-paragraph-dy');
    return dataParagraphDy
      ? dataParagraphDy.split(' ').filter(Boolean).map(m => Math.round(m * 100) / 100)
      : 0;
  }

  getWordSpacing () {
    const match = this.textClass.match(/word-spacing-([-+]?[0-9]*\.?[0-9]+)/i);
    const wordSpacing = match
      ? parseFloat(match[1])
      : parseFloat(this.getDomAttribute('word-spacing', 0));

    return !wordSpacing && this.tspans.length
      ? parseFloat(this.tspans[0].getAttribute('word-spacing')) || 0
      : wordSpacing;
  }

  getLineMargin () {
    const match = this.textClass.match(/ line-margin-([-+]?[0-9]*\.?[0-9]+)/i);
    if (match) return parseFloat(match[1]);

    const dataMargin = this.text.dom.getAttribute('data-margin');
    return dataMargin
      ? dataMargin.split(' ').filter(Boolean).map(m => Math.round(m * 100) / 100)
      : 0;
  }

  getFirstLineMargin () {
    const match = this.textClass.match(/first-line-margin-([-+]?[0-9]*\.?[0-9]+)/i);
    if (match) return parseFloat(match[1]);

    const dataMargin = this.text.dom.getAttribute('data-first-line-margin');
    return dataMargin
      ? dataMargin.split(' ').filter(Boolean).map(m => Math.round(m * 100) / 100)
      : 0;
  }

  getRightMargin () {
    const match = this.textClass.match(/right-margin-([-+]?[0-9]*\.?[0-9]+)/i);
    if (match) return parseFloat(match[1]);

    const dataMargin = this.text.dom.getAttribute('data-right-margin');
    return dataMargin
      ? dataMargin.split(' ').filter(Boolean).map(m => Math.round(m * 100) / 100)
      : 0;
  }

  getLineSpacing (fontSize) {
    const textElement = this.text.dom;
    const globalDy = textElement.getAttribute('data-dy');
    const tspans = this.tspans;

    let lineSpacing;
    if (globalDy) {
      lineSpacing = globalDy.split(' ').filter(Boolean).map(dy => Math.round(dy * 100) / 100);
    } else {
      lineSpacing = (/line-spacing-([-+]?[0-9]*\.?[0-9]+)/.test(this.textClass))
        ? parseFloat(this.textClass.match(/line-spacing-([-+]?[0-9]*\.?[0-9]+)/i)[1])
        : 0;

      if (!textElement.classList.contains('text') && !lineSpacing && tspans.length >= 2) {
        if (tspans[1].getAttribute('y')) {
          lineSpacing = parseFloat(tspans[1].getAttribute('y') - tspans[0].getAttribute('y') - fontSize);
        }
      }
    }

    return lineSpacing || 0;
  }

  getTextColumns () {
    const match = this.textClass.match(/columns-([-+]?[0-9]*)/);

    return match ? parseInt(match[1]) : 1;
  }

  getTextColumnsGap () {
    return Number(this.text.dom.dataset.columnsGap) || constants.text.columns.defaultGap;
  }

  getTextColumnsBalance () {
    const columnsBalance = this.text.dom.dataset.balanceColumns;
    return columnsBalance ? Number(this.text.dom.dataset.balanceColumns) : 1;
  }

  getOrderedFill () {
    return this.text.dom.querySelector('.ordered')?.getAttribute('fill') || null;
  }

  getOrderedWidth () {
    return Number(this.text.dom.querySelector('.ordered')?.getAttribute('data-ordered-width')) || null;
  }

  getBulletFill () {
    return this.text.dom.querySelector('.bullets')?.getAttribute('fill') || null;
  }

  getBulletWidth () {
    return Number(this.text.dom.querySelector('.bullets')?.getAttribute('data-bullet-width')) || null;
  }

  getTextOverflow () {
    return !!(this.textareaDom.dataset.parent || this.textareaDom.dataset.child);
  }
}
